<template>
  <div class="block pb-6">
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="InquiryList" label="Inquiries" />
      <breadcrumb-link view="BatchInquiryCreate" label="Batch Report" :active="true" />
    </breadcrumb-list>

    <div class="container">
      <div class="box">
        <h1 class="title">Run as Batch Report</h1>
        <p class="subtitle is-6 has-text-grey">
          Re-run each selected inquiry exactly as it was last submitted. Each re-run creates a
          brand-new inquiry and uses one unit of your subscription.
        </p>

        <hr />

        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div>
                <p class="heading">Inquiries selected</p>
                <p class="title">{{ rows.length }}</p>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div class="buttons">
              <b-button
                type="is-info"
                icon-left="refresh"
                :loading="running"
                :disabled="rows.length === 0"
                @click="runBatchInquiry"
              >
                Run the batch
              </b-button>
              <b-button
                type="is-success"
                icon-left="account-multiple-check"
                :loading="runningAll"
                @click="runAllUnarchived"
              >
                Run all unarchived patients
              </b-button>
            </div>
          </div>
        </div>

        <!-- Selected patients, filterable by name -->
        <template v-if="rows.length > 0">
          <b-field>
            <b-input
              v-model="patientFilter"
              placeholder="Filter by patient name"
              icon="magnify"
              rounded
            />
          </b-field>

          <b-table :data="filteredRows" striped hoverable paginated per-page="15">
            <b-table-column field="patient" label="Patient" v-slot="props">
              {{ props.row.patient || '—' }}
            </b-table-column>
            <b-table-column field="payer_name" label="Payer" v-slot="props">
              {{ props.row.payer_name || '—' }}
            </b-table-column>
            <b-table-column field="id" label="Inquiry ID" v-slot="props">
              <code>{{ props.row.id }}</code>
            </b-table-column>
            <b-table-column label="" v-slot="props" width="40">
              <b-button
                type="is-text"
                icon-left="close"
                size="is-small"
                @click="removeRow(props.row.id)"
              />
            </b-table-column>

            <template #empty>
              <section class="section has-text-centered">
                <p>No patients match "{{ patientFilter }}".</p>
              </section>
            </template>
          </b-table>
        </template>

        <!-- Ineligible inquiries (block-on-ineligible) -->
        <b-notification
          v-if="ineligible.length > 0"
          type="is-danger"
          :closable="false"
          class="mt-4"
        >
          <p class="has-text-weight-semibold mb-2">
            This batch can't run until the following inquiries are deselected:
          </p>
          <ul>
            <li v-for="item in ineligible" :key="item.id">
              <code>{{ item.id }}</code> — {{ item.reason }}
            </li>
          </ul>
          <p class="mt-2">
            <b-button tag="router-link" :to="{ name: 'InquiryList' }" size="is-small">
              Back to inquiries
            </b-button>
          </p>
        </b-notification>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../request';

export default {
  name: 'BatchInquiryCreate',
  data() {
    return {
      rows: [],
      ineligible: [],
      patientFilter: '',
      running: false,
      runningAll: false,
    };
  },
  computed: {
    inquiryIds() {
      return this.rows.map((r) => r.id);
    },
    filteredRows() {
      const term = this.patientFilter.trim().toLowerCase();
      if (!term) return this.rows;
      return this.rows.filter((r) => (r.patient || '').toLowerCase().includes(term));
    },
  },
  created() {
    const storedRows = sessionStorage.getItem('batchInquiryRows');
    if (storedRows) {
      this.rows = JSON.parse(storedRows);
    } else {
      const storedIds = sessionStorage.getItem('batchInquiryIds');
      const ids = storedIds ? JSON.parse(storedIds) : [];
      this.rows = ids.map((id) => ({ id: String(id), patient: '', payer_name: '' }));
    }
  },
  methods: {
    removeRow(id) {
      this.rows = this.rows.filter((r) => r.id !== id);
    },
    async runBatchInquiry() {
      if (this.inquiryIds.length === 0) return;
      this.running = true;
      this.ineligible = [];
      try {
        const response = await request.post('/batch-inquiries', { inquiry_ids: this.inquiryIds });
        this.clearStash();
        this.$router.push({ name: 'BatchInquiryReport', params: { id: response.data.batch_id } });
      } catch (e) {
        if (e.response && e.response.status === 422 && e.response.data.ineligible) {
          this.ineligible = e.response.data.ineligible;
        } else {
          this.$buefy.toast.open({ message: 'Failed to start the batch.', type: 'is-danger' });
        }
      } finally {
        this.running = false;
      }
    },
    runAllUnarchived() {
      this.$buefy.dialog.confirm({
        title: 'Run all unarchived patients',
        message:
          'This re-runs every active (unarchived) patient — one re-run per patient — and uses ' +
          'one subscription unit each. Archive any patients you no longer track before running.',
        confirmText: 'Run all unarchived',
        type: 'is-success',
        onConfirm: async () => {
          this.runningAll = true;
          this.ineligible = [];
          try {
            const response = await request.post('/batch-inquiries', { all_unarchived: true });
            this.clearStash();
            this.$router.push({ name: 'BatchInquiryReport', params: { id: response.data.batch_id } });
          } catch (e) {
            const errors = (e.response && e.response.data && e.response.data.errors) || {};
            const msg = errors.NOFIELD || Object.values(errors)[0] || 'Failed to start the batch.';
            this.$buefy.toast.open({ message: msg, type: 'is-danger' });
          } finally {
            this.runningAll = false;
          }
        },
      });
    },
    clearStash() {
      sessionStorage.removeItem('batchInquiryIds');
      sessionStorage.removeItem('batchInquiryRows');
    },
  },
};
</script>
